/**
 * Director PendingBacklogPanel — "Pending > N days" backlog with age indicator.
 *
 * Pure presentational: accepts `items[]` from the parent page (fetched via
 * the existing `/api/v1/insights/pending-backlog` endpoint, which Director
 * has access to as an HOD+ role). RLS surfaces all managers' backlogs.
 *
 * Each row shows an urgency-coded age indicator so the Director can see
 * which managers are sitting on requests past the SLA.
 *
 * Self-clears when items is empty. No × button.
 */
import type { CSSProperties } from 'react'
import { Card } from '../primitives/Card'

export type BacklogUrgency = 'critical' | 'high' | 'normal'

export interface PendingBacklogItem {
  leaveId: string
  ageDays: number
  employee: string
  type: string
  dates: string
  urgency: BacklogUrgency
}

export interface PendingBacklogPanelProps {
  items: PendingBacklogItem[]
  /** When true, renders a loading placeholder. */
  loading?: boolean
}

// Urgency thresholds (working days).
const CRITICAL_THRESHOLD = 10
const HIGH_THRESHOLD = 5

/** Derive urgency from ageDays if not already set. */
export function urgencyForAge(ageDays: number): BacklogUrgency {
  if (ageDays >= CRITICAL_THRESHOLD) return 'critical'
  if (ageDays >= HIGH_THRESHOLD) return 'high'
  return 'normal'
}

const URGENCY_TOKENS: Record<BacklogUrgency, { fg: string; bg: string; border: string }> = {
  critical: {
    fg: 'var(--danger-fg)',
    bg: 'var(--danger-bg)',
    border: 'var(--danger-line)',
  },
  high: {
    fg: 'var(--warning-fg)',
    bg: 'var(--warning-bg)',
    border: 'var(--warning-line)',
  },
  normal: {
    fg: 'var(--text-3)',
    bg: 'var(--surface-2)',
    border: 'var(--border)',
  },
}

/**
 * Director-context pending-backlog panel.
 *
 * Each row shows the leave request stuck in a manager's queue beyond SLA.
 * Age indicator uses urgency tokens: ≥10 days = critical red, ≥5 days =
 * warning amber, <5 days = quiet grey.
 */
export function PendingBacklogPanel({
  items,
  loading = false,
}: PendingBacklogPanelProps): JSX.Element | null {
  if (!loading && items.length === 0) return null

  const sorted = [...items].sort((a, b) => b.ageDays - a.ageDays)

  return (
    <Card padding="14px 18px" data-testid="director-pending-backlog-panel">
      <div style={headerStyle}>
        <h2 style={titleStyle}>Approvals stuck in queue</h2>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {items.length} request{items.length === 1 ? '' : 's'} past SLA
        </span>
      </div>

      {loading ? (
        <div style={emptyStyle}>Loading…</div>
      ) : (
        <>
          <div style={gridHeaderStyle}>
            <span>Employee</span>
            <span>Type</span>
            <span>Dates</span>
            <span style={{ textAlign: 'right' }}>Age</span>
          </div>
          <ul role="list" style={listStyle}>
            {sorted.map((item) => {
              const tokens = URGENCY_TOKENS[item.urgency]
              return (
                <li
                  key={item.leaveId}
                  data-testid="director-backlog-row"
                  data-urgency={item.urgency}
                  style={gridRowStyle}
                >
                  <span style={employeeStyle}>{item.employee}</span>
                  <span style={typeStyle}>{item.type}</span>
                  <span style={datesStyle}>{item.dates}</span>
                  <span
                    data-testid="director-backlog-age"
                    style={{
                      ...ageBadgeStyle,
                      color: tokens.fg,
                      background: tokens.bg,
                      borderColor: tokens.border,
                    }}
                  >
                    {item.ageDays}d
                  </span>
                </li>
              )
            })}
          </ul>
          <div style={captionStyle}>
            These requests are sitting unactioned past the SLA. A nudge to the approving manager usually clears them.
          </div>
        </>
      )}
    </Card>
  )
}

// ── styles ────────────────────────────────────────────────────────────────────

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: 10,
}

const titleStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  margin: 0,
}

const gridHeaderStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, 2fr) 100px 1fr 60px',
  gap: 8,
  fontSize: 10,
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-3)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  padding: '6px 0',
  borderBottom: '1px solid var(--border)',
}

const listStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
}

const gridRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, 2fr) 100px 1fr 60px',
  gap: 8,
  padding: '10px 0',
  borderBottom: '1px solid var(--line-subtle)',
  alignItems: 'center',
  fontSize: 13,
}

const employeeStyle: CSSProperties = {
  fontWeight: 600,
  color: 'var(--text)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const typeStyle: CSSProperties = {
  color: 'var(--text-2)',
  textTransform: 'capitalize',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const datesStyle: CSSProperties = {
  color: 'var(--text-2)',
  fontSize: 12,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const ageBadgeStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  fontFamily: 'var(--font-mono)',
  padding: '3px 8px',
  borderRadius: 8,
  border: '1px solid',
  textAlign: 'right',
  justifySelf: 'end',
  whiteSpace: 'nowrap',
}

const captionStyle: CSSProperties = {
  marginTop: 10,
  fontSize: 12,
  color: 'var(--text-3)',
  lineHeight: 1.5,
}

const emptyStyle: CSSProperties = {
  padding: '20px 0',
  textAlign: 'center',
  color: 'var(--text-3)',
  fontSize: 13,
}
